import { CanMatchFn, Routes, UrlSegment } from '@angular/router';
import { authenticationChildRoutes } from '@features/auth/auth.routes';
import { isMarketingWebsiteHost } from '@core/boot/marketing-host-redirect';
import { BlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { lazyCanActivate } from '@core/guards/lazy-guard.util';
import { DASHBOARD_VIEW_PERMISSIONS } from '@core/auth/nav-route-permissions.config';
import { STOREFRONT_PERMISSIONS } from '@core/auth/storefront-permissions.config';
import { RESERVED_PUBLIC_SLUGS } from '@core/boot/public-boot-path.util';
import { ensureI18nNamespacesGuard } from '@core/i18n/ensure-i18n-namespaces.guard';
import { PublicNotFoundPageComponent } from './features/product-catalog/presentation/pages/public-not-found/public-not-found-page.component';
import { writeLastPublicStoreSlug } from './features/product-catalog/utils/catalog-last-store-slug.storage';

export { isMarketingWebsiteHost } from '@core/boot/marketing-host-redirect';

/**
 * Guards del panel admin cargados perezosamente: `/:slug` y demás rutas públicas
 * nunca deben bajar auth/tenant/planes en el bundle inicial (ver docs/DEVELOPMENT.md).
 */
const authGuard = lazyCanActivate(() => import('@core/guards/auth.guard').then((m) => m.authGuard));
const companyGuard = lazyCanActivate(() =>
  import('@core/guards/company.guard').then((m) => m.companyGuard)
);
const onboardGuard = lazyCanActivate(() =>
  import('@core/guards/onboard.guard').then((m) => m.onboardGuard)
);
const permissionGuard = lazyCanActivate(() =>
  import('@core/guards/permission.guard').then((m) => m.permissionGuard)
);
const tenantGuard = lazyCanActivate(() =>
  import('@core/guards/tenant.guard').then((m) => m.tenantGuard)
);
const superadminGuard = lazyCanActivate(() =>
  import('@core/guards/superadmin.guard').then((m) => m.superadminGuard)
);
const planModuleMyPageGuard = lazyCanActivate(() =>
  import('@core/plan/plan-module.guard').then((m) => m.planModuleMyPageGuard)
);
/** Operador solo-POS: no puede abrir shell tenant por URL (`/sales/*`, etc.). */
const enforcePosOnlyShellGuard = lazyCanActivate(() =>
  import('@core/guards/enforce-pos-only-shell.guard').then((m) => m.enforcePosOnlyShellGuard)
);

const publicCatalogSlugCanMatch: CanMatchFn = (_route, segments: UrlSegment[]) => {
  const segment = segments[0]?.path?.trim().toLowerCase() ?? '';
  return segment.length > 0 && !RESERVED_PUBLIC_SLUGS.has(segment);
};

const loadPublicCatalog = () =>
  import('./features/product-catalog/product-catalog.routes').then(
    (m) => m.ProductCatalogRoutes
  );

/** Primer segmento de rutas marketing prerenderizadas en la raíz (ver `app.routes.server.ts`). */
const MARKETING_ROOT_FIRST_SEGMENTS = new Set([
  'precios',
  'plan',
  'que-es-milinco',
  'para',
]);

/**
 * Sin hostname de navegador (prerender SSG): solo activar el árbol marketing
 * para rutas que realmente se prerenderizan. Evita pintar la landing en
 * `/starter`, `/pos`, etc. cuando el motor SSR evalúa canMatch sin `location`.
 */
export const isMarketingWebsiteRootPath = (segments: readonly UrlSegment[]): boolean => {
  if (segments.length === 0) {
    return true;
  }
  const first = segments[0]?.path?.trim().toLowerCase() ?? '';
  return MARKETING_ROOT_FIRST_SEGMENTS.has(first);
};

const marketingWebsiteRootCanMatch: CanMatchFn = (_route, segments) => {
  const hostname = globalThis.location?.hostname?.trim().toLowerCase() ?? '';
  if (hostname) {
    return isMarketingWebsiteHost(hostname);
  }
  return isMarketingWebsiteRootPath(segments);
};

export const routes: Routes = [
  {
    path: '',
    canMatch: [marketingWebsiteRootCanMatch],
    component: BlankComponent,
    canActivate: [ensureI18nNamespacesGuard],
    data: { i18nNamespaces: ['website', 'storefront'], marketingWebsiteRoot: true },
    loadChildren: () =>
      import('./features/website/website.routes').then((m) => m.WebsiteRoutes),
  },
  {
    path: '',
    component: FullComponent,
    canActivate: [ensureI18nNamespacesGuard],
    canActivateChild: [enforcePosOnlyShellGuard],
    data: { i18nNamespaces: ['planUpgrade'] },
    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'starter',
        canActivate: [
          authGuard,
          companyGuard,
          tenantGuard,
          planModuleMyPageGuard,
          permissionGuard,
          ensureI18nNamespacesGuard,
        ],
        data: {
          permission: STOREFRONT_PERMISSIONS.profile,
          i18nNamespaces: ['starter', 'onboard', 'catalog', 'planChange', 'website'],
        },
        loadChildren: () =>
          import('./features/starter/starter.routes').then((m) => m.StarterRoutes),
      },
      {
        path: 'dashboard',
        canActivate: [authGuard, companyGuard, tenantGuard, permissionGuard],
        data: { permissionAnyOf: DASHBOARD_VIEW_PERMISSIONS },
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.DashboardRoutes),
      },
      {
        path: 'storefront',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['website', 'onboard', 'storefront', 'catalog'] },
        loadChildren: () =>
          import('./features/storefront/storefront.routes').then((m) => m.StorefrontRoutes),
      },
      {
        path: 'profile',
        canActivate: [authGuard, companyGuard],
        loadChildren: () =>
          import('./features/profile/profile.routes').then((m) => m.ProfileRoutes),
      },
      {
        path: 'catalog',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['catalog', 'services', 'storefront'] },
        loadChildren: () =>
          import('./features/catalog/catalog.routes').then((m) => m.FeatureCatalogRoutes),
      },
      {
        path: 'inventory',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['inventory'] },
        loadChildren: () =>
          import('./features/inventory/inventory.routes').then((m) => m.InventoryRoutes),
      },
      {
        path: 'sales',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['sales', 'pos', 'customers', 'storefront', 'finance'] },
        loadChildren: () => import('./features/sales/sales.routes').then((m) => m.SalesRoutes),
      },
      {
        path: 'pos',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['pos', 'catalog', 'customers', 'storefront', 'sales', 'finance'] },
        loadChildren: () => import('./features/pos/pos.routes').then((m) => m.PosRoutes),
      },
      {
        path: 'customers',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['customers', 'storefront'] },
        loadChildren: () =>
          import('./features/customers/customers.routes').then((m) => m.CustomersRoutes),
      },
      {
        path: 'finance',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['finance'] },
        loadChildren: () =>
          import('./features/finance/finance.routes').then((m) => m.FinanceRoutes),
      },
      {
        path: 'purchases',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['purchases', 'finance'] },
        loadChildren: () =>
          import('./features/purchases/purchases.routes').then((m) => m.PurchasesRoutes),
      },
      {
        path: 'payroll',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        // `sales` = tip roles/payouts UI aún bajo features/sales (ver docs/PAYROLL_BASIC.md).
        data: { i18nNamespaces: ['payroll', 'sales'] },
        loadChildren: () =>
          import('./features/payroll/payroll.routes').then((m) => m.PayrollRoutes),
      },
      {
        path: 'reports',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['reports', 'sales'] },
        loadChildren: () =>
          import('./features/reports/reports.routes').then((m) => m.ReportsRoutes),
      },
      {
        path: 'services',
        redirectTo: 'catalog/services',
        pathMatch: 'full',
      },
      {
        path: 'settings',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['settings'] },
        loadChildren: () =>
          import('./features/settings/settings.routes').then((m) => m.SettingsRoutes),
      },
      {
        path: 'account',
        canActivate: [authGuard, companyGuard, tenantGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['milincoAccount', 'planChange', 'website', 'planUpgrade'] },
        loadChildren: () =>
          import('./features/milinco-account/milinco-account.routes').then(
            (m) => m.MilincoAccountRoutes
          ),
      },
      {
        path: 'platform',
        canActivate: [authGuard, superadminGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['platform', 'onboard'] },
        loadChildren: () =>
          import('./features/platform/platform.routes').then((m) => m.PlatformRoutes),
      },
    ],
  },
  {
    path: '',
    component: BlankComponent,
    children: [
      {
        path: 'authentication',
        children: authenticationChildRoutes,
      },
      {
        path: 'not-found',
        component: PublicNotFoundPageComponent,
      },
      {
        path: 'oauth/authorize',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/oauth/pages/oauth-authorize/oauth-authorize.component').then(
            (m) => m.OauthAuthorizeComponent
          ),
      },
      {
        path: 'onboard',
        canActivate: [authGuard, onboardGuard, ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['onboard', 'catalog', 'storefront', 'settings'] },
        loadChildren: () =>
          import('./features/onboard/onboard.routes').then((m) => m.OnboardRoutes),
      },
      {
        path: 'product-catalog',
        loadChildren: loadPublicCatalog,
      },
      {
        path: 'legal',
        canActivate: [ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['website'] },
        loadChildren: () => import('./features/legal/legal.routes').then((m) => m.LegalRoutes),
      },
      {
        path: 'website',
        canActivate: [ensureI18nNamespacesGuard],
        data: { i18nNamespaces: ['website', 'storefront'] },
        loadChildren: () =>
          import('./features/website/website.routes').then((m) => m.WebsiteRoutes),
      },
      {
        path: 'catalog/:slug',
        redirectTo: (snapshot) => `/${snapshot.params['slug']}`,
      },
      {
        path: ':slug',
        canMatch: [publicCatalogSlugCanMatch],
        loadChildren: loadPublicCatalog,
      },
    ],
  },
  {
    path: '**',
    redirectTo: ({ url }) => {
      const path = `/${url.map((segment) => segment.path).join('/')}`;
      const from = path === '/' ? '' : path;
      // Recordar slug de tienda aunque la ruta hija no exista (CTA de 404).
      const first = url[0]?.path?.trim().toLowerCase() ?? '';
      if (first && !RESERVED_PUBLIC_SLUGS.has(first)) {
        writeLastPublicStoreSlug(first);
      }
      return from
        ? `not-found?from=${encodeURIComponent(from)}`
        : 'not-found';
    },
  },
];
